import { Fragment, ReactNode, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Message } from "./Message";
import { SendMessageForm } from "./SendMessageForm";
import { SharedDocumentMessage } from "./SharedDocumentMessage";
import { parseMessageContent } from "./parseMessageContent";
import DocumentMetadata from "../document/DocumentMetadata";
import { dayKey, formatDaySeparator, formatMessageTime } from "./messageTime";

export interface ConversationViewProps {
  // The own id on the own server (owner of the documents shared into the chat) ...
  userId: string;
  // ... and on the chat's server (`chatOrigin`, undefined = the own one), where messages are
  // sent under it; the default is `userId` (F8).
  selfId?: string;
  chatOrigin?: string;
  // The foreign servers the contact may name in shared documents (foreignOriginsOf).
  foreignOrigins?: string[];
  ownerId: string;
  chatId: string;
  sharedKey: JsonWebKey;
  messages: Message[];
  onMessageSent: (message: Message) => void;
  // The other chat members to push-notify (ADR 0020), passed straight through
  // to SendMessageForm.
  notify: string[];
  share: (document: DocumentMetadata) => Promise<void>;
  shareDisabled?: boolean;
  shareDisabledHint?: string;
  // The 1:1 chat's counterpart - passed through to SharedDocumentMessage so
  // it can open the image detail view. Omitted for a group conversation.
  contactUserId?: string;
  // Group context (ADR 0019 decision 4), passed through to
  // SharedDocumentMessage for its Access-Path resolution - omitted for a 1:1
  // chat.
  group?: {
    groupId: string;
    groupOwnerId: string;
    groupKey: JsonWebKey;
    // The own id on the group's server (`chatOrigin`) - the sharer id the Access-Path is
    // relative to; the default is `userId`.
    selfId?: string;
  };
  // Renders a "group-invitation" message - only ever posted into a 1:1 chat,
  // so omitted by a group's own conversation. A message that parses as an
  // invitation but has no renderer (should not happen) falls back to plain
  // text.
  renderInvitation?: (invitation: {
    groupId: string;
    owner: string;
    name: string;
    origin?: string | null;
    address?: string;
  }) => ReactNode;
  // Renders a "shared-folder" message - only ever posted into a 1:1 chat.
  renderSharedFolder?: (folder: {
    documentId: string;
    owner: string;
    name: string;
    contributions: boolean;
    origin?: string | null;
  }) => ReactNode;
  // Renders a sender label (name/avatar) above someone else's message - only
  // used in a group conversation (see useGroupMemberProfiles); a 1:1 chat has
  // exactly one counterpart and needs no per-message label.
  renderSender?: (senderId: string) => ReactNode;
}

// The message list + composer shared by the 1:1 chat view (Chat.tsx) and a
// group's own conversation (GroupChat.tsx, ADR 0019): both are "history of
// messages under (ownerId, chatId), encrypted with sharedKey" - what differs
// is how a shared image is reached (a 1:1 direct grant vs. a group Access-
// Path) and, in a group, who sent a given message.
export function ConversationView({
  userId,
  selfId = userId,
  chatOrigin,
  foreignOrigins,
  ownerId,
  chatId,
  sharedKey,
  messages,
  onMessageSent,
  notify,
  share,
  shareDisabled,
  shareDisabledHint,
  contactUserId,
  group,
  renderInvitation,
  renderSharedFolder,
  renderSender,
}: ConversationViewProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // A folder shared again says what the share is now: the newest message about it wins, also
  // when an older one is opened.
  const latestContributions = new Map<string, boolean>();
  for (const m of messages) {
    const parsed = parseMessageContent(m.content);
    if (parsed.type === "shared-folder") {
      latestContributions.set(parsed.documentId, parsed.contributions);
    }
  }

  return (
    <>
      <div
        className="scroll padding vertical"
        style={{
          flexGrow: 1,
          gap: "0.5rem",
        }}
      >
        {messages.map((m, index) => {
          // A day separator whenever a message starts a new day.
          const separator =
            index === 0 ||
            dayKey(m.timestamp) !== dayKey(messages[index - 1].timestamp)
              ? formatDaySeparator(m.timestamp, i18n.language, t)
              : undefined;
          const isMine = m.sender === selfId;
          const parsed = parseMessageContent(m.content);
          return (
            <Fragment key={m.id}>
              {separator && (
                <div className="center-align">
                  <span className="chip">{separator}</span>
                </div>
              )}
              <div
                className={`padding elevate ${
                  isMine
                    ? "primary top-round left-round"
                    : "surface-container top-round right-round"
                }`}
                style={{
                  alignSelf: isMine ? "flex-end" : "flex-start",
                  maxWidth: "80%",
                  wordWrap: "break-word",
                }}
              >
                {!isMine && renderSender?.(m.sender)}
                {parsed.type === "shared-document" ? (
                  <SharedDocumentMessage
                    documentId={parsed.documentId}
                    owner={parsed.owner}
                    origin={parsed.origin}
                    chatOrigin={chatOrigin}
                    foreignOrigins={foreignOrigins}
                    chatKey={group ? undefined : sharedKey}
                    contactUserId={contactUserId}
                    group={group}
                  />
                ) : parsed.type === "group-invitation" && renderInvitation ? (
                  renderInvitation(parsed)
                ) : parsed.type === "shared-folder" && renderSharedFolder ? (
                  renderSharedFolder({
                    ...parsed,
                    contributions:
                      latestContributions.get(parsed.documentId) ??
                      parsed.contributions,
                  })
                ) : parsed.type === "text" ? (
                  parsed.content
                ) : (
                  m.content
                )}
                <div className="right-align" style={{ opacity: 0.7 }}>
                  <small>
                    <time dateTime={m.timestamp}>
                      {formatMessageTime(m.timestamp, i18n.language)}
                    </time>
                  </small>
                </div>
              </div>
            </Fragment>
          );
        })}
        <div ref={messagesEndRef} />
      </div>
      <hr className="divider" />
      <SendMessageForm
        userId={userId}
        selfId={selfId}
        chatOrigin={chatOrigin}
        ownerId={ownerId}
        chatId={chatId}
        sharedKey={sharedKey}
        onMessageSent={onMessageSent}
        notify={notify}
        share={share}
        shareDisabled={shareDisabled}
        shareDisabledHint={shareDisabledHint}
      />
    </>
  );
}
